import { useState, useEffect } from "react";
import { api } from "@/App";
import { ClipboardCheck, Plus, Trash2, GripVertical, Save, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const DEFAULTS = [
  "Pression des pneus", "Contrôle éclairage", "Freinage avant", "Freinage arrière",
  "Fuite amortisseur", "Silentblocs", "Jeux dans les roues", "Essai routier", "Remise à zéro entretien",
];

export default function ChecklistTemplateConfig() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchTemplate(); }, []);

  const fetchTemplate = async () => {
    setLoading(true);
    try {
      const res = await api.get("/checklist-template");
      setItems(res.data.items || []);
    } catch (e) {
      toast.error("Erreur de chargement");
    }
    setLoading(false);
  };

  const updateLabel = (idx, label) => setItems((p) => p.map((it, i) => i === idx ? { ...it, label } : it));
  const addItem = () => setItems((p) => [...p, { id: "", label: "", order: p.length }]);
  const removeItem = (idx) => setItems((p) => p.filter((_, i) => i !== idx));
  const move = (idx, dir) => {
    setItems((p) => {
      const arr = [...p];
      const j = idx + dir;
      if (j < 0 || j >= arr.length) return p;
      [arr[idx], arr[j]] = [arr[j], arr[idx]];
      return arr;
    });
  };

  const handleSave = async () => {
    const clean = items.filter((it) => it.label.trim());
    if (clean.length === 0) { toast.error("Ajoutez au moins un point de contrôle"); return; }
    setSaving(true);
    try {
      const res = await api.put("/checklist-template", { items: clean.map((it, i) => ({ id: it.id || "", label: it.label.trim(), order: i })) });
      setItems(res.data.items || []);
      toast.success("Check-list enregistrée");
    } catch (e) {
      toast.error("Erreur d'enregistrement");
    }
    setSaving(false);
  };

  const restoreDefaults = () => {
    setItems(DEFAULTS.map((l, i) => ({ id: "", label: l, order: i })));
    toast.info("Liste par défaut chargée — pensez à enregistrer");
  };

  return (
    <Card className="border-slate-200" data-testid="checklist-config">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><ClipboardCheck className="h-5 w-5" /> Check-list de contrôle mécanique</CardTitle>
        <CardDescription>
          Ces points de contrôle s'affichent au mécanicien sur les véhicules en <strong>mécanique</strong> (après la saisie du kilométrage, ou via le bouton "Check-list"). Chaque point se remplit en OK / Défaut / N-A.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="flex justify-center py-8"><div className="spinner" /></div>
        ) : (
          <>
            <div className="space-y-2">
              {items.map((it, idx) => (
                <div key={idx} className="flex items-center gap-2" data-testid={`tpl-item-${idx}`}>
                  <div className="flex flex-col">
                    <button type="button" onClick={() => move(idx, -1)} className="text-slate-400 hover:text-slate-700 leading-none" title="Monter">▲</button>
                    <button type="button" onClick={() => move(idx, 1)} className="text-slate-400 hover:text-slate-700 leading-none" title="Descendre">▼</button>
                  </div>
                  <Input value={it.label} onChange={(e) => updateLabel(idx, e.target.value)} placeholder="Point de contrôle" data-testid={`tpl-input-${idx}`} />
                  <Button variant="ghost" size="icon" className="text-red-600 shrink-0" onClick={() => removeItem(idx)} data-testid={`tpl-del-${idx}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button variant="outline" onClick={addItem} data-testid="tpl-add-btn"><Plus className="h-4 w-4 mr-2" /> Ajouter un point</Button>
              <Button variant="outline" onClick={restoreDefaults}><RotateCcw className="h-4 w-4 mr-2" /> Liste par défaut</Button>
              <Button className="bg-red-600 hover:bg-red-700 ml-auto" onClick={handleSave} disabled={saving} data-testid="tpl-save-btn">
                <Save className="h-4 w-4 mr-2" /> {saving ? "Enregistrement..." : "Enregistrer"}
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
